import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question, SelectOptionDefault, SelectOptionGroup } from '../../../config/types';
import { isObject } from '../../../utils/array-utils';
import {
  getConditionalQuestionDefaultValues,
  getConditionalQuestionNames,
} from '../../../utils/config-utils';
import { validateField } from '../../../utils/form-utils';
import {
  getSingleSelectableOptionValue,
  resolveSelectOptions,
} from '../../../utils/select-assets';
import DCFMarkdown from '../../DCFMarkdown/DCFMarkdown';
import AutocompleteWrapper from '../Autocomplete/AutocompleteWrapper';
import ConditionalCheckboxField from '../CheckboxGroup/ConditionalCheckboxField';
import { DateInputField } from '../DateInputField/DateInputField';
import { ShortDateInputField } from '../DateInputField/ShortDateInputField';
import Details from '../Details';
import ErrorMessage from '../ErrorMessage';
import OptionalLabel from '../OptionalLabel';
import Radio from '../Radio';
import SelectInputField from '../SelectInputField';
import TextInputField from '../TextInputField';
import Textarea from '../Textarea';
import ConditionalRadioField from './ConditionalRadioField';
import { DefaultRadio } from './DefaultRadio';
import type {
  ConditionalRadioProps,
  RadioGroupProps,
  RadioItemProps,
  RadioSubGroupProps,
} from './Props';

const ConditionalRadio = ({
  id,
  anchorId,
  index,
  item,
  currentValue,
  name,
  disabled,
  showRequiredFlag,
  assets,
  handleOnChange,
}: ConditionalRadioProps) => {
  const value = item.option;
  const fieldId = !!anchorId && index === 0 ? anchorId : `${id}${value}${anchorId}`;
  const questions = (item.questions as Question[]) ?? [];
  const showConditional = !!questions.length && value === currentValue;
  const conditionalFieldId = `${fieldId}_conditional`;

  const {
    setValue,
    unregister,
    watch,
    formState: { errors },
  } = useFormContext();
  const allValues = watch();

  const previousValue = useRef<string>();

  useEffect(() => {
    if (previousValue.current === value && currentValue !== value) {
      const resetValues = getConditionalQuestionDefaultValues(questions);
      Object.entries(resetValues).forEach(([questionName, resetValue]) => {
        setValue(questionName, resetValue, { shouldDirty: true, shouldValidate: false });
      });
      unregister(getConditionalQuestionNames(questions), {
        keepDefaultValue: true,
        keepValue: true,
      });
    }
    previousValue.current = currentValue;
  }, [currentValue, questions, setValue, unregister, value]);

  return (
    <>
      <div className="govuk-radios__item" key={fieldId}>
        <Radio
          id={fieldId}
          name={name}
          disabled={disabled}
          value={value}
          checked={currentValue === value}
          data-aria-controls={conditionalFieldId}
          aria-expanded={showConditional}
          onChange={(e) => handleOnChange(e)}
        />
        <label className="govuk-label govuk-radios__label" htmlFor={fieldId}>
          {value}
        </label>
      </div>
      {showConditional && (
        <div
          className={
            showConditional
              ? 'govuk-radios__conditional'
              : 'govuk-radios__conditional govuk-radios__conditional--hidden'
          }
          id={conditionalFieldId}
          hidden={!showConditional}
        >
          {questions.map((question) => {
            const rules = question?.rules || {};
            const hideSingleOptionChildSelect =
              question.questionType === 'select' &&
              getSingleSelectableOptionValue(
                resolveSelectOptions({
                  options: question.options,
                  questionName: question.name,
                  assets,
                  answers: allValues,
                }),
              ) !== undefined;

            if (question.questionType === 'select' && hideSingleOptionChildSelect) {
              return (
                <SelectInputField
                  key={question.name}
                  name={question.name}
                  id={`${id}${question.name}`}
                  hintOption={question.hintOption}
                  options={question.options || []}
                  assets={assets}
                  disabled={disabled}
                  rules={rules}
                  title={question.title}
                  errorMessages={question.errorMessages}
                  detailsHeading={question.detailsHeading}
                  detailsContent={question.detailsContent}
                  defaultValue={
                    typeof question.defaultValue === 'string' ? question.defaultValue : undefined
                  }
                  autoFillSingleOptionAndHide
                />
              );
            }

            return (
              <div
                className={`govuk-form-group ${
                  errors?.[question.name] && 'govuk-form-group--error'
                }`}
                key={question.name}
              >
                <label
                  className={`govuk-label ${
                    showRequiredFlag === true && rules?.required === true
                      ? 'required-question-label'
                      : ''
                  }`}
                  htmlFor={`${id}${question.name}`}
                >
                  <OptionalLabel text={question.title} optional={rules?.required !== true} />
                </label>
                {question.hint && <DCFMarkdown content={question.hint} type="hint-text" />}
                {question.questionType === 'textInput' && (
                    <TextInputField
                      name={question.name}
                      approver={question.approver}
                      allowSelfApprover={question.allowSelfApprover}
                      restrictDomains={question.restrictDomains}
                      readOnly={question.readOnly}
                      disabled={disabled ?? false}
                      id={`${id}${question.name}`}
                      rules={rules}
                    title={question.title}
                    placeholder={question.placeholder}
                    wordCount={question.wordCount}
                    characterCount={question.characterCount}
                    errorMessages={question.errorMessages}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'textarea' && (
                  <Textarea
                    name={question.name}
                    disabled={disabled ?? false}
                    numeric={!!question.rules?.number || undefined}
                    id={`${id}${question.name}`}
                    rules={rules}
                    title={question.title}
                    placeholder={question.placeholder}
                    wordCount={question.wordCount}
                    characterCount={question.characterCount}
                    textRows={question.textRows}
                    errorMessages={question.errorMessages}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'radio' && (
                  <ConditionalRadioField
                    question={question}
                    disabled={disabled ?? false}
                    id={`${id}${question.name}`}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    assets={assets}
                  />
                )}
                {question.questionType === 'date' && (
                  <DateInputField
                    question={question}
                    anchorId={`${id}${question.name}`}
                    disabled={disabled}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'shortDate' && (
                  <ShortDateInputField
                    question={question}
                    anchorId={`${id}${question.name}`}
                    disabled={disabled}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'checkbox' && (
                  <ConditionalCheckboxField
                    question={question}
                    disabled={disabled ?? false}
                    id={`${id}${question.name}`}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    assets={assets}
                  />
                )}
                {question.questionType === 'select' && (
                  <SelectInputField
                    name={question.name}
                    id={`${id}${question.name}`}
                    hintOption={question.hintOption}
                    options={question.options || []}
                    assets={assets}
                    disabled={disabled}
                    rules={rules}
                    title={question.title}
                    errorMessages={question.errorMessages}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    defaultValue={
                      typeof question.defaultValue === 'string' ? question.defaultValue : undefined
                    }
                    showRequiredFlag={showRequiredFlag}
                  />
                )}
                {question.questionType === 'autoComplete' && (
                  <AutocompleteWrapper
                    id={id}
                    items={question.items || []}
                    options={Array.isArray(question.options) ? question.options : []}
                    name={question.name}
                    title={question.title}
                    rules={rules}
                    matchLimit={question.matchLimit}
                    fuzzyMatch={question.fuzzyMatch}
                    placeholder={question.searchPlaceholder || question.placeholder}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    showRequiredFlag={showRequiredFlag}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const RadioItem = ({
  id,
  anchorId,
  index,
  item,
  currentValue,
  name,
  disabled,
  showRequiredFlag,
  assets,
  handleOnChange,
}: RadioItemProps) => {
  const isConditionalRadio = isObject(item) && 'questions' in item;
  return (
    <>
      {isConditionalRadio && (
        <ConditionalRadio
          id={id}
          anchorId={anchorId}
          name={name}
          disabled={disabled}
          showRequiredFlag={showRequiredFlag}
          index={index}
          item={item as SelectOptionDefault}
          currentValue={currentValue}
          assets={assets}
          handleOnChange={handleOnChange}
        />
      )}
      {!isConditionalRadio && (
        <DefaultRadio
          id={id}
          name={name}
          disabled={disabled}
          anchorId={anchorId}
          index={index}
          item={item as string}
          currentValue={currentValue}
          handleOnChange={handleOnChange}
        />
      )}
    </>
  );
};

const RadioSubGroup = ({
  id,
  anchorId,
  currentValue,
  option,
  name,
  disabled,
  showRequiredFlag,
  assets,
  handleOnChange,
}: RadioSubGroupProps) => (
  <div className="govuk-radios fieldset-container" data-module="govuk-radios" key={option.label}>
    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
      <h3 className="govuk-fieldset__heading">{option.label}</h3>
    </legend>
    {option.options.map((option, index) => (
      <RadioItem
        id={id}
        name={name}
        disabled={disabled}
        showRequiredFlag={showRequiredFlag}
        key={option}
        anchorId={anchorId}
        index={index}
        item={option as string | SelectOptionDefault}
        currentValue={currentValue}
        assets={assets}
        handleOnChange={handleOnChange}
      />
    ))}
  </div>
);

export default function RadioGroup({
  question,
  anchorId,
  disabled,
  id,
  showRequiredFlag,
  detailsHeading,
  detailsContent,
  assets,
}: RadioGroupProps) {
  const { register, getFieldState, getValues, setValue, watch } = useFormContext();
  const { options, name, title, rules, errorMessages } = question;
  const resolvedOptions = Array.isArray(options) ? options : [];
  const defaultValue = typeof question.defaultValue === 'string' ? question.defaultValue : '';
  const watchedValue = watch(name);
  const currentValue = watchedValue ?? defaultValue;

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(name, e.target.value, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, rules || {}, value, errorMessages),
  });
  const { error } = getFieldState(name);

  const optionIsSubGroup: boolean =
    (resolvedOptions[0] && typeof resolvedOptions[0] === 'object' && 'label' in resolvedOptions[0]) ||
    false;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-radios" data-module="govuk-radios">
        {resolvedOptions.map((option, i) => (
          <React.Fragment key={i}>
            {optionIsSubGroup && (
              <RadioSubGroup
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                showRequiredFlag={showRequiredFlag}
                anchorId={anchorId}
                option={option as SelectOptionGroup}
                currentValue={currentValue}
                assets={assets}
                handleOnChange={handleOnChange}
              />
            )}
            {!optionIsSubGroup && (
              <RadioItem
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                showRequiredFlag={showRequiredFlag}
                anchorId={anchorId}
                index={i}
                item={option as string | SelectOptionDefault}
                currentValue={currentValue}
                assets={assets}
                handleOnChange={handleOnChange}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

